/* global chrome */
import React, { useState, useEffect, useContext, useMemo, useCallback, Suspense } from 'react';
import { X, Mail, Phone, Briefcase, Building2, MapPin, GraduationCap, FileText, Star, Tag, Calendar, Loader2, GitBranch, File, MessageSquare, Clock, CheckSquare, MoveUpRight, Info, Trash2, Edit, Bell } from 'lucide-react';
import { FaLinkedin } from 'react-icons/fa';
import { ApiContext } from '../../App';
import InfoTab from '../candidateDetails/InfoTab';
import { toast } from 'react-toastify';
import AddReminder from '../Reminders/AddReminder';
import SendEmailModal from '../SendEmailModal';
import ConfirmationDialog from '../utils/ConfirmationDialog';

// Lazy loading des onglets
const NotesTab = React.lazy(() => import('../candidateDetails/NotesTab'));
const ProcessTab = React.lazy(() => import('../candidateDetails/ProcessTab'));
const CVTab = React.lazy(() => import('../candidateDetails/CVTab'));
const ActivityTab = React.lazy(() => import('../candidateDetails/ActivityTab'));
const TasksTab = React.lazy(() => import('../candidateDetails/TasksTab'));
const ConversationTab = React.lazy(() => import('../candidateDetails/ConversationTab'));
const LinkedInTab = React.lazy(() => import('../candidateDetails/LinkedInTab'));

const IndividualSidebar = ({ isOpen, onClose, individual: initialIndividual, theme = 'dark', onStageUpdate }) => {
  const API = useContext(ApiContext);
  const [activeTab, setActiveTab] = useState('information');
  const [individual, setIndividual] = useState(initialIndividual);
  const [taskCount, setTaskCount] = useState(0);
  const [processCount, setProcessCount] = useState(0);
  const [hasCV, setHasCV] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [linkedinConnectionStatus, setLinkedinConnectionStatus] = useState(null);
  const [loadingConnectionStatus, setLoadingConnectionStatus] = useState(false);

  // Debug: log activeTab changes
  useEffect(() => {
    console.log('Active tab changed to:', activeTab);
  }, [activeTab]);

  // Toggle statut "À l'écoute d'opportunités"
  const toggleOpenStatus = useCallback(async () => {
    if (!individual) return;

    const newOpenToJobOpportunities = !individual.openToJobOpportunities;

    try {
      const response = await API.put(`/api/individuals/${individual._id}/open`, {
        openToJobOpportunities: newOpenToJobOpportunities
      });
      if (response.data.success) {
        setIndividual(prev => ({
          ...prev,
          openToJobOpportunities: newOpenToJobOpportunities
        }));
        toast.success(newOpenToJobOpportunities ? 'Marqué comme à l\'écoute' : 'Marqué comme non disponible');
      }
    } catch (error) {
      console.error('Error updating the status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  }, [individual, API]);

  // Supprimer l'individu
  const deleteIndividual = useCallback(async () => {
    try {
      await API.delete(`/api/individuals/${individual._id}`);
      toast.success('Profil supprimé');
      onClose();
    } catch (error) {
      console.error('Error deleting individual:', error);
      toast.error('Erreur lors de la suppression');
    }
  }, [individual, API, onClose]);

  // Synchroniser individual avec les props
  useEffect(() => {
    setIndividual(initialIndividual);
  }, [initialIndividual]);

  // Récupérer le statut de connexion LinkedIn
  useEffect(() => {
    if (!individual?.linkedinUrl) return;

    const checkConnectionStatus = async () => {
      // Vérifier si l'extension Chrome est disponible
      if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
        return;
      }

      setLoadingConnectionStatus(true);
      const EXTENSION_ID = 'ahggehojcdmojmdoacambcmchehpiioo';

      chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'get-linkedin-connection-status',
          data: {
            linkedinUrl: individual.linkedinUrl,
          },
        },
        (response) => {
          if (chrome.runtime.lastError) {
            console.error('Extension error:', chrome.runtime.lastError);
            setLoadingConnectionStatus(false);
            return;
          }

          if (response && response.success) {
            setLinkedinConnectionStatus({
              isConnected: response.isConnected,
              networkDistance: response.networkDistance,
            });
          }
          setLoadingConnectionStatus(false);
        }
      );
    };

    checkConnectionStatus();
  }, [individual?.linkedinUrl]);

  // Récupérer les compteurs pour les badges
  useEffect(() => {
    if (!individual || !individual._id) return;

    const fetchTabCounts = async () => {
      try {
        // Récupérer le nombre de tâches non terminées
        const remindersResponse = await API.get(`/api/reminders/${individual._id}`);
        if (remindersResponse.data.success && Array.isArray(remindersResponse.data.reminders)) {
          const incompleteTasks = remindersResponse.data.reminders.filter(reminder => !reminder.done);
          setTaskCount(incompleteTasks.length);
        }

        // Récupérer le nombre de processus actifs
        const [recruitmentResponse, bizdevResponse] = await Promise.allSettled([
          API.get(`/api/positions/individual/${individual._id}/processes`),
          API.get(`/api/process-bizdev/individual/${individual._id}/processes`)
        ]);

        let totalActiveProcesses = 0;
        if (recruitmentResponse.status === 'fulfilled' && Array.isArray(recruitmentResponse.value.data)) {
          totalActiveProcesses += recruitmentResponse.value.data.length;
        }
        if (bizdevResponse.status === 'fulfilled' && Array.isArray(bizdevResponse.value.data)) {
          totalActiveProcesses += bizdevResponse.value.data.length;
        }
        setProcessCount(totalActiveProcesses);

        // Vérifier la présence d'un CV
        setHasCV(!!individual.resume);
      } catch (error) {
        console.error('Error fetching tab counts:', error);
      }
    };

    fetchTabCounts();
  }, [individual, API]);

  // Helper pour afficher les badges
  const renderTabBadge = useCallback((count, label) => {
    if (label === 'cv') {
      return count > 0 ? (
        <span className="ml-2 bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[1.25rem] h-5 flex items-center justify-center">
          1
        </span>
      ) : null;
    }

    if (!count || count === 0) return null;

    return (
      <span className="ml-2 bg-indigo-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[1.25rem] h-5 flex items-center justify-center">
        {count > 99 ? '99+' : count}
      </span>
    );
  }, []);

  // Définition des onglets
  const tabs = useMemo(() => {
    if (!individual || !individual._id) return [];

    return [
    {
        id: 'information',
        label: 'Information',
        icon: <Info size={18} />,
        component: (
        <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <InfoTab
              individual={individual}
              onIndividualUpdate={(updatedIndividual) => setIndividual(updatedIndividual)}
              onStageUpdate={onStageUpdate}
            />
        </Suspense>
    )
    },
    {
        id: 'activity',
        label: 'Activité',
        icon: <Clock size={18} />,
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <ActivityTab individualId={individual._id} />
          </Suspense>
        )
      },
      /*
      {
        id: 'conversation',
        label: 'Conversation',
        icon: <Mail size={18} />,
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <ConversationTab individualId={individual._id} individual={individual} />
          </Suspense>
        )
      },
      */
      {
        id: 'linkedin',
        label: 'LinkedIn',
        icon: <FaLinkedin size={18} />,
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <LinkedInTab individualId={individual._id} individual={individual} />
          </Suspense>
        )
      },
      {
        id: 'notes',
        label: 'Notes',
        icon: <MessageSquare size={18} />,
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <NotesTab individualId={individual._id} className='text-slate-800' />
          </Suspense>
        )
      },
      {
        id: 'tasks',
        label: 'Tâches',
        icon: <CheckSquare size={18} />,
        badge: renderTabBadge(taskCount, 'tasks'),
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <TasksTab individualId={individual._id} />
          </Suspense>
        )
      },
      /*
      {
        id: 'process',
        label: 'Processus',
        icon: <GitBranch size={18} />,
        badge: renderTabBadge(processCount, 'process'),
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <ProcessTab individualId={individual._id} />
          </Suspense>
        )
      },
      */
      {
        id: 'cv',
        label: 'CV',
        icon: <File size={18} />,
        badge: renderTabBadge(hasCV ? 1 : 0, 'cv'),
        component: (
          <Suspense fallback={<div className="p-4 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
            <CVTab
              individual={individual}
              individualId={individual._id}
              API={API}
              onCVUpdate={(updatedIndividual) => setIndividual(updatedIndividual)}
            />
          </Suspense>
        )
      }
    ];

  }, [individual, API, taskCount, processCount, hasCV, renderTabBadge]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black z-40 ${
          isOpen ? 'animate-fadeIn' : 'animate-fadeOut'
        }`}
        style={{
          animation: isOpen ? 'fadeIn 0.3s ease-out forwards' : 'fadeOut 0.3s ease-out forwards',
          opacity: 0
        }}
        onClick={onClose}
      />
      
      <div
        className="fixed top-0 right-0 h-screen w-full sm:w-3/4 md:w-2/3 lg:w-2/3 xl:w-2/3 bg-white text-white z-50 shadow-2xl flex flex-col overflow-hidden"
        style={{
          animation: isOpen ? 'slideIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards' : 'slideOut 0.3s ease-in forwards',
          transform: 'translateX(100%)'
        }}
      >
        {/* État de chargement */}
        {!individual ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-600" />
              <p className="text-sm text-slate-500">Chargement des informations...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Section gauche : Photo + Info */}
                <div className='flex items-center gap-3 sm:gap-4 flex-1 min-w-0'>
                    {/* Photo */}
                    {individual.photo && (
                    <div className="flex-shrink-0">
                        <img src={individual.photo} alt={individual.Name} className='w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border border-gray-300'/>
                    </div>
                    )}

                    {/* Nom et poste */}
                    <div className='flex-1 min-w-0'>
                        <div className='flex items-center space-x-2 mb-1 flex-wrap'>
                            <h2 className="text-base sm:text-xl font-semibold truncate m-0 text-gray-800">
                            {individual.Name}
                            </h2>
                            {individual.linkedinUrl && (
                              <div className="flex items-center gap-2">
                                <a
                                  href={individual.linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-shrink-0 hover:opacity-70 transition-opacity"
                                  title="Voir le profil LinkedIn"
                                >
                                  <FaLinkedin size={18} className="text-blue-600" />
                                </a>
                                {loadingConnectionStatus ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                ) : linkedinConnectionStatus ? (
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      linkedinConnectionStatus.isConnected
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                    title={linkedinConnectionStatus.isConnected ? 'Connecté sur LinkedIn' : 'Non connecté sur LinkedIn'}
                                  >
                                    {linkedinConnectionStatus.isConnected ? '✓ Connecté' : 'Non connecté'}
                                  </span>
                                ) : null}
                              </div>
                            )}
                        </div>

                        {individual.jobTitle && (
                            <p className="text-xs sm:text-sm text-gray-600 truncate">
                            {individual.jobTitle}
                            </p>
                        )}
                    </div>
                </div>

                  {/* Toggle et boutons d'action */}
                  <div className="flex items-center gap-2 flex-wrap justify-between sm:justify-end">
                    {/* Toggle À l'écoute */}
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-gray-200">
                      <span className="text-xs text-gray-600">
                        {individual.openToJobOpportunities ? 'À l\'écoute' : 'Pas dispo'}
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={individual.openToJobOpportunities || false}
                          onChange={toggleOpenStatus}
                          className="sr-only peer"
                        />
                        <div className={`relative w-9 h-5 rounded-full peer transition-colors duration-300 ${
                          individual.openToJobOpportunities ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          <div className={`absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-300 ${
                            individual.openToJobOpportunities ? 'translate-x-4' : 'translate-x-0'
                          }`}></div>
                        </div>
                      </label>
                    </div>

                    {/* Boutons d'action */}
                    {/*
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                      title="Modifier le profil"
                    >
                      <Edit size={16} className="text-gray-600" />
                    </button>
                    */}

                    <button
                      onClick={() => setShowSendEmailModal(true)}
                      className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                      title="Envoyer un email"
                    >
                      <Mail size={16} className="text-gray-600" />
                    </button>

                    <button
                      onClick={() => setShowReminderModal(true)}
                      className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                      title="Ajouter une tâche"
                    >
                      <Bell size={16} className="text-gray-600" />
                    </button>

                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="p-1.5 hover:bg-red-50 rounded transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-gray-50 border-b">
              <nav className="flex overflow-x-auto scrollbar-hide" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      text-gray-800 flex items-center space-x-1 py-2 px-3 sm:py-3  sm:px-2  border-b-2 font-medium text-xs sm:text-xs transition-colors whitespace-nowrap flex-shrink-0
                      ${activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-800 hover:text-gray-500 hover:border-gray-300'
                      }
                    `}
                  >
                    <span className="hidden sm:inline">{tab.icon}</span>
                    <span>{tab.label}</span>
                    {tab.badge}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto bg-gray-100 px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
              {(() => {
                const activeTabContent = tabs.find(tab => tab.id === activeTab);
                console.log('Rendering tab:', activeTab, 'Found:', !!activeTabContent, 'Total tabs:', tabs.length);

                if (!activeTabContent) {
                  return <div className="text-white p-4">Aucun onglet trouvé pour: {activeTab}</div>;
                }

                return (
                  <div className="h-full  rounded-lg  overflow-y-auto text-gray-800">
                    {activeTabContent.component}
                  </div>
                );
              })()}
            </div>

          </>
        )}
      </div>

      {/* Modales */}
      {showReminderModal && individual && (
        <AddReminder
          individualId={individual._id}
          API={API}
          onClose={() => setShowReminderModal(false)}
        />
      )}

      {showSendEmailModal && individual && (
        <SendEmailModal
          recipients={[individual._id]}
          onClose={() => setShowSendEmailModal(false)}
          onSend={() => setShowSendEmailModal(false)}
        />
      )}

      {/* Modale de confirmation de suppression */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={deleteIndividual}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le profil de ${individual?.Name || 'ce candidat'} ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 0.5; }
        }

        @keyframes fadeOut {
          from { opacity: 0.5; }
          to { opacity: 0; }
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        @keyframes slideOut {
          from { transform: translateX(0); }
          to { transform: translateX(100%); }
        }

        /* Masquer la scrollbar tout en gardant le scroll */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default IndividualSidebar;